import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';

export const useEventProducts = (eventId, productTypeFilter) => {
  const client = generateClient();
  const [isLoading, setIsLoading] = useState(true);
  const [eventProducts, setEventProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);

  useEffect(() => {
    if (eventId) {
      fetchData();
    }
  }, [eventId, productTypeFilter]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        fetchAvailableProducts(),
        fetchEventProducts()
      ]);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableProducts = async () => {
    let nextToken = null;
    let allProducts = [];

    do {
      try {
        const filter = productTypeFilter === 'marketing'
          ? { type: { eq: "marketing_booth" } }
          : {
              and: [
                { type: { ne: "marketing_booth" } },
                { type: { ne: "event_booth" } },
                { type: { ne: "event_booth_partner" } },
                { type: { ne: "event_space" } },
                { type: { ne: "event_ticket" } }
              ]
            };

        const response = await client.graphql({
          query: /* GraphQL */ `
            query SearchProducts(
              $filter: SearchableProductFilterInput
              $limit: Int
              $nextToken: String
            ) {
              searchProducts(
                filter: $filter
                limit: $limit
                nextToken: $nextToken
              ) {
                items {
                  id
                  name {
                    language
                    text
                  }
                  sku
                  description {
                    language
                    text
                  }
                  price
                  type
                  subType
                  icon
                  stock
                  image {
                    main {
                      fileName
                    }
                  }
                }
                nextToken
              }
            }
          `,
          variables: {
            filter,
            limit: 1000,
            nextToken
          }
        });

        allProducts = [...allProducts, ...response.data.searchProducts.items];
        nextToken = response.data.searchProducts.nextToken;
      } catch (error) {
        console.error('Error fetching available products:', error);
        break;
      }
    } while (nextToken);

    setAvailableProducts(allProducts);
  };

  const fetchEventProducts = async () => {
    let nextToken = null;
    let allEventProducts = [];

    do {
      try {
        const response = await client.graphql({
          query: /* GraphQL */ `
            query ListEventProducts(
              $filter: ModelEventProductFilterInput
              $limit: Int
              $nextToken: String
            ) {
              listEventProducts(
                filter: $filter
                limit: $limit
                nextToken: $nextToken
              ) {
                items {
                  id
                  event {
                    id
                  }
                  product {
                    id
                    name {
                      language
                      text
                    }
                    sku
                    description {
                      language
                      text
                    }
                    price
                    type
                    subType
                    icon
                    stock
                    image {
                      main {
                        fileName
                      }
                    }
                  }
                  price
                  stock
                  isAvailable
                  sortKey
                  timeRangePrices {
                    timeRange {
                      start
                      end
                    }
                    price
                  }
                }
                nextToken
              }
            }
          `,
          variables: {
            filter: { eventEventProductsId: { eq: eventId } },
            limit: 1000,
            nextToken
          }
        });

        const items = response.data.listEventProducts.items;

        // Filter based on product type
        const filteredItems = productTypeFilter === 'marketing'
          ? items.filter(item => item.product.type === 'marketing_booth')
          : items.filter(item =>
              item.product.type !== 'marketing_booth' &&
              item.product.type !== 'event_booth' &&
              item.product.type !== 'event_booth_partner' &&
              item.product.type !== 'event_space' &&
              item.product.type !== 'event_ticket'
            );

        allEventProducts = [...allEventProducts, ...filteredItems];
        nextToken = response.data.listEventProducts.nextToken;
      } catch (error) {
        console.error('Error fetching event products:', error);
        break;
      }
    } while (nextToken);

    // Sort by sortKey if exists, otherwise maintain original order
    allEventProducts.sort((a, b) => {
      if (a.sortKey !== null && a.sortKey !== undefined &&
          b.sortKey !== null && b.sortKey !== undefined) {
        return a.sortKey - b.sortKey;
      }
      return 0;
    });

    setEventProducts(allEventProducts);
  };

  const addProductToEvent = async (productId, price = null, stock = null) => {
    try {
      const product = availableProducts.find(p => p.id === productId);
      if (!product) return;

      const maxSortKey = Math.max(...eventProducts.map(ep => ep.sortKey || 0), 0);

      const response = await client.graphql({
        query: /* GraphQL */ `
          mutation CreateEventProduct($input: CreateEventProductInput!) {
            createEventProduct(input: $input) {
              id
              product {
                id
                name {
                  language
                  text
                }
                sku
                description {
                  language
                  text
                }
                price
                type
                subType
                icon
                stock
                image {
                  main {
                    fileName
                  }
                }
              }
              price
              stock
              isAvailable
              sortKey
              timeRangePrices {
                timeRange {
                  start
                  end
                }
                price
              }
            }
          }
        `,
        variables: {
          input: {
            eventEventProductsId: eventId,
            productEventProductsId: productId,
            price: price || product.price,
            stock: stock || product.stock,
            isAvailable: true,
            sortKey: maxSortKey + 1
          }
        }
      });

      setEventProducts([...eventProducts, response.data.createEventProduct]);
    } catch (error) {
      console.error('Error adding product to event:', error);
    }
  };

  const removeProductFromEvent = async (eventProductId) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation DeleteEventProduct($input: DeleteEventProductInput!) {
            deleteEventProduct(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: { id: eventProductId }
        }
      });

      setEventProducts(eventProducts.filter(ep => ep.id !== eventProductId));
    } catch (error) {
      console.error('Error removing product from event:', error);
    }
  };

  const updateEventProduct = async (eventProductId, updates) => {
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          mutation UpdateEventProduct($input: UpdateEventProductInput!) {
            updateEventProduct(input: $input) {
              id
              price
              stock
              isAvailable
              sortKey
              timeRangePrices {
                timeRange {
                  start
                  end
                }
                price
              }
            }
          }
        `,
        variables: {
          input: {
            id: eventProductId,
            ...updates
          }
        }
      });

      setEventProducts(eventProducts.map(ep =>
        ep.id === eventProductId
          ? { ...ep, ...response.data.updateEventProduct }
          : ep
      ));
    } catch (error) {
      console.error('Error updating event product:', error);
    }
  };

  const reorderProducts = async (newOrder) => {
    // Update local state immediately for responsive UI
    setEventProducts(newOrder);

    // Update sortKey for all items
    try {
      const updates = newOrder.map((item, index) => ({
        id: item.id,
        sortKey: index
      }));

      // Batch update all items
      await Promise.all(
        updates.map(update =>
          client.graphql({
            query: /* GraphQL */ `
              mutation UpdateEventProduct($input: UpdateEventProductInput!) {
                updateEventProduct(input: $input) {
                  id
                  sortKey
                }
              }
            `,
            variables: {
              input: update
            }
          })
        )
      );
    } catch (error) {
      console.error('Error reordering products:', error);
      // Revert on error
      await fetchEventProducts();
    }
  };

  return {
    isLoading,
    eventProducts,
    availableProducts,
    addProductToEvent,
    removeProductFromEvent,
    updateEventProduct,
    reorderProducts
  };
};